import { apiClient } from '../../api/fetchClient';
import {
  ContentAvailabilityResponseType,
  DenialReason,
  GatedAccessResponseType,
} from '../../content/models/access-response';
import { t } from '../../i18n';
import type {
  AlbumAccessResponse,
  DownloadFormatType,
  MusicIndustryParty,
} from '../models/album.interface';

export interface DownloadResponse {
  url: string;
}

// Short-lived album cache so an app block remounting after a theme section
// re-render (variant selection replaces the section DOM) doesn't refetch and
// flash a loading state. Short TTL keeps the token-refresh path honest: a
// Mux token error happens long after this expires, so the refetch is real.
const ALBUM_CACHE_TTL_MS = 5 * 60 * 1000;
const albumCache = new Map<string, { ts: number; data: AlbumAccessResponse }>();

async function cached(
  key: string,
  load: () => Promise<AlbumAccessResponse>
): Promise<AlbumAccessResponse> {
  const hit = albumCache.get(key);
  if (hit && Date.now() - hit.ts < ALBUM_CACHE_TTL_MS) {
    return structuredClone(hit.data);
  }
  const data = await load();
  albumCache.set(key, { ts: Date.now(), data });
  return data;
}

/** Drop cached albums so the next load refetches (e.g. Mux token refresh). */
export function invalidateAlbumCache(): void {
  albumCache.clear();
}

export class MusicService {
  async loadAlbum(albumId: string): Promise<AlbumAccessResponse> {
    const accessCodes = new URLSearchParams(window.location.search).get('access')?.split(',');
    const accessCode = accessCodes?.[0];
    const options = accessCode ? { headers: { 'x-single-access-code': accessCode } } : {};
    return cached(`album:${albumId}`, () =>
      apiClient.get<AlbumAccessResponse>(
        `/apps/single/v3/content/album/${albumId}`,
        undefined,
        options
      )
    );
  }

  async loadAlbumByProductId(productId: string): Promise<AlbumAccessResponse> {
    const url = new URL('/apps/single/v3/content/album', window.location.origin);

    if (productId) {
      url.searchParams.append('productId', productId);
    }
    return cached(`product:${productId}`, () =>
      apiClient.get<AlbumAccessResponse>(url.pathname + url.search)
    );
  }

  async loadDemoAlbum(): Promise<AlbumAccessResponse> {
    const testArtist: MusicIndustryParty = {
      ids: [],
      type: 'ARTIST',
      subType: 'MAIN',
      order: 0,
      name: t('music.demoArtist'),
      images: [],
      additionalInfo: '',
      individuals: [],
    };
    const track = (n: number, duration: string) => ({
      ids: [],
      title: t('music.demoTrack', { n: String(n) }),
      order: n,
      duration,
      description: '',
      lyrics: '',
      explicit: false,
      previewAvailable: true,
      publishingLine: '',
      copyrightLine: '',
      releaseDate: '',
      presaleDate: null,
      parties: [testArtist],
    });
    return Promise.resolve({
      id: '__demo__',
      authenticated: false,
      loginTypes: [],
      availabilityResponse: ContentAvailabilityResponseType.UNRESTRICTED,
      denialReason: DenialReason.UNKNOWN,
      accessResponse: GatedAccessResponseType.ACCESS_GRANTED,
      content: {
        ids: [],
        title: t('music.demoAlbum'),
        description: '',
        genres: [],
        publishingLine: '',
        copyrightLine: '',
        copyrightRegistrationNumber: '',
        releaseDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        presaleDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        parties: [testArtist],
        tracks: [track(1, '3:45'), track(2, '4:12'), track(3, '3:30')],
        images: [{ type: 'FRONT_COVER', order: 0, url: 'https://placehold.net/600x600.png' }],
        attachedFiles: [],
        audioFormats: [],
      },
    });
  }

  async downloadAlbum(albumId: string, format: DownloadFormatType): Promise<DownloadResponse> {
    const accessCodes = new URLSearchParams(window.location.search).get('access')?.split(',');
    const accessCode = accessCodes?.[1];
    const options = accessCode ? { headers: { 'x-single-access-code': accessCode } } : {};
    return apiClient.get<DownloadResponse>(
      `/apps/single/v3/content/album/${albumId}/download/${format}`,
      undefined,
      options
    );
  }
}

export const musicService = new MusicService();
